import { CheckCircle2, Download, Printer, ShieldCheck, UploadCloud } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { SubmissionSummary } from "../../lib/formSubmissionSummary";

interface BaseFieldProps {
  name: string;
  label: string;
  required?: boolean;
  error?: string;
  className?: string;
  helperText?: string;
}

interface TextFieldProps extends BaseFieldProps {
  type?: string;
  autoComplete?: string;
}

export function FormSection({
  id,
  number,
  title,
  formName,
  children,
}: {
  id: string;
  number: string;
  title: string;
  formName: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      className="scroll-mt-28 rounded-lg border border-[#D94A16]/22 bg-white p-5 shadow-[0_22px_60px_rgba(45,32,8,.08)] sm:p-6"
    >
      <div className="mb-5 flex items-start gap-4 border-b border-[#D94A16]/16 pb-4">
        <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[#050505] text-sm font-black text-[#FF6A2B]">
          {number}
        </span>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{formName}</p>
          <h2 id={`${id}-heading`} className="mt-1 font-display text-2xl font-bold text-[#050505]">
            {title}
          </h2>
        </div>
      </div>
      {children}
    </section>
  );
}

export function TextField({ name, label, required, error, className = "", type = "text", autoComplete, helperText }: TextFieldProps) {
  const describedBy = [helperText ? `${name}-helper` : "", error ? `${name}-error` : ""].filter(Boolean).join(" ") || undefined;

  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={describedBy}
        className="form-field mt-2 min-h-12 w-full rounded-lg px-4 py-3 text-base focus:outline-none"
      />
      <FieldError name={name} error={error} />
    </div>
  );
}

export function TextareaField({ name, label, required, error, className = "", helperText, rows = 3 }: BaseFieldProps & { rows?: number }) {
  const describedBy = [helperText ? `${name}-helper` : "", error ? `${name}-error` : ""].filter(Boolean).join(" ") || undefined;
  const fieldSpacing = helperText ? "mt-3" : "mt-2";

  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <textarea
        id={name}
        name={name}
        rows={rows}
        required={required}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={describedBy}
        className={`form-field ${fieldSpacing} w-full rounded-lg px-4 py-3 text-base leading-7 focus:outline-none`}
      />
      <FieldError name={name} error={error} />
    </div>
  );
}

export function SelectField({ name, label, required, error, className = "", options }: BaseFieldProps & { options: string[] }) {
  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      <select
        id={name}
        name={name}
        required={required}
        defaultValue=""
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${name}-error` : undefined}
        className="form-field mt-2 min-h-12 w-full rounded-lg px-4 py-3 text-base focus:outline-none"
      >
        <option value="" disabled>
          Select one
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <FieldError name={name} error={error} />
    </div>
  );
}

export function FileField({ name, label, className = "", accept, multiple, helperText, required, error }: BaseFieldProps & { accept?: string; multiple?: boolean }) {
  const describedBy = [helperText ? `${name}-helper` : "", error ? `${name}-error` : ""].filter(Boolean).join(" ") || undefined;
  const fieldSpacing = helperText ? "mt-3" : "mt-2";

  return (
    <div className={className}>
      <FieldLabel name={name} label={label} required={required} />
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <label
        className={`${fieldSpacing} flex min-h-24 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-5 text-center text-sm font-bold text-[#5c5246] transition hover:border-[#D94A16]/60 hover:bg-white ${
          error ? "border-red-400 bg-red-50" : "border-[#D94A16]/36 bg-[#fff9ef]"
        }`}
      >
        <UploadCloud className="size-6 text-[#D94A16]" aria-hidden="true" />
        Choose file{multiple ? "s" : ""}
        <input
          id={name}
          name={name}
          type="file"
          accept={accept}
          multiple={multiple}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={describedBy}
          className="sr-only"
        />
      </label>
      <FieldError name={name} error={error} />
    </div>
  );
}

export function RadioGroupField({
  name,
  label,
  options,
  required,
  error,
  helperText,
  className = "",
  onChange,
}: BaseFieldProps & { options: string[]; onChange?: (value: string) => void }) {
  const describedBy = [helperText ? `${name}-helper` : "", error ? `${name}-error` : ""].filter(Boolean).join(" ") || undefined;

  return (
    <fieldset className={className} aria-invalid={error ? "true" : "false"} aria-describedby={describedBy}>
      <legend className="text-sm font-black text-[#16120a]">
        {label} {required ? <RequiredMark /> : null}
      </legend>
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {options.map((option) => (
          <label
            key={option}
            className={`flex min-h-12 items-start gap-3 rounded-lg border bg-[#fffdf8] p-4 text-sm font-semibold leading-7 text-[#4f4538] ${
              error ? "border-red-400" : "border-[#D94A16]/22"
            }`}
          >
            <input
              name={name}
              type="radio"
              value={option}
              required={required}
              className="mt-1 size-5 shrink-0 accent-[#050505]"
              onChange={(event) => {
                if (event.currentTarget.checked) onChange?.(option);
              }}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
      <FieldError name={name} error={error} />
    </fieldset>
  );
}

export function CheckboxGroupField({
  name,
  label,
  options,
  required,
  error,
  className = "",
  helperText,
  onChange,
}: BaseFieldProps & { options: string[]; onChange?: (option: string, checked: boolean) => void }) {
  const describedBy = [helperText ? `${name}-helper` : "", error ? `${name}-error` : ""].filter(Boolean).join(" ") || undefined;

  return (
    <fieldset className={className} aria-invalid={error ? "true" : "false"} aria-describedby={describedBy}>
      <legend className="text-sm font-black text-[#16120a]">
        {label} {required ? <RequiredMark /> : null}
      </legend>
      {helperText ? (
        <p id={`${name}-helper`} className="mt-2 rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3 text-sm font-semibold leading-7 text-[#5c5246]">
          {helperText}
        </p>
      ) : null}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {options.map((option) => (
          <label
            key={option}
            className={`flex min-h-12 items-start gap-3 rounded-lg border bg-[#fffdf8] p-4 text-sm font-semibold leading-7 text-[#4f4538] ${
              error ? "border-red-400" : "border-[#D94A16]/22"
            }`}
          >
            <input
              name={name}
              type="checkbox"
              value={option}
              className="mt-1 size-5 shrink-0 accent-[#050505]"
              onChange={(event) => onChange?.(option, event.currentTarget.checked)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
      <FieldError name={name} error={error} />
    </fieldset>
  );
}

export function CheckboxField({ name, label, required, error }: BaseFieldProps) {
  return (
    <div>
      <label
        className={`flex gap-3 rounded-lg border bg-[#fffdf8] p-4 text-sm font-semibold leading-7 text-[#4f4538] ${
          error ? "border-red-400" : "border-[#D94A16]/22"
        }`}
      >
        <input
          id={name}
          name={name}
          type="checkbox"
          required={required}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={error ? `${name}-error` : undefined}
          className="mt-1 size-5 shrink-0 accent-[#050505]"
        />
        <span>
          {label} {required ? <RequiredMark /> : null}
        </span>
      </label>
      <FieldError name={name} error={error} />
    </div>
  );
}

export function TurnstilePlaceholder() {
  return (
    <div className="rounded-lg border border-[#D94A16]/22 bg-[#fff9ef] p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[#050505] text-[#FF6A2B]">
          <ShieldCheck className="size-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-sm font-black text-[#050505]">Security verification</p>
          <p className="mt-1 text-sm font-semibold leading-6 text-[#5c5246]">
            Cloudflare Turnstile verification will appear here when connected.
          </p>
        </div>
      </div>
    </div>
  );
}

export function ThankYouState({
  backLink = "/",
  message,
  onDownloadPdf,
  onPrint,
  summary,
}: {
  backLink?: string;
  message: string;
  onDownloadPdf: () => void;
  onPrint: () => void;
  summary: SubmissionSummary | null;
}) {
  return (
    <section className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-5xl gap-6">
        <div className="print-hide rounded-lg border border-[#D94A16]/24 bg-white p-6 text-center shadow-[0_24px_70px_rgba(45,32,8,.12)] sm:p-8">
          <span className="mx-auto grid size-16 place-items-center rounded-full bg-[#050505] text-[#FF6A2B]">
            <CheckCircle2 className="size-9" aria-hidden="true" />
          </span>
          <h2 className="mt-6 font-display text-4xl font-bold text-[#050505]">Thank you.</h2>
          <p className="mx-auto mt-4 max-w-3xl text-lg leading-8 text-[#5c5246]">{message}</p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onPrint}
              className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#050505] px-5 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#16120a]"
            >
              <Printer className="size-4" aria-hidden="true" />
              Print My Form
            </button>
            <button
              type="button"
              onClick={onDownloadPdf}
              className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-[#D94A16]/35 bg-[#fff9ef] px-5 text-sm font-extrabold text-[#050505] transition hover:bg-white"
            >
              <Download className="size-4" aria-hidden="true" />
              Download My Form as PDF
            </button>
            <Link
              to={backLink}
              className="focus-ring inline-flex min-h-12 items-center justify-center rounded-lg border border-[#D94A16]/30 px-5 text-sm font-extrabold text-[#5c5246] transition hover:bg-[#fff9ef]"
            >
              Back to Forms Portal
            </Link>
          </div>
        </div>

        {summary ? <SubmittedFormSummary summary={summary} /> : null}
      </div>
    </section>
  );
}

export function SubmittedFormSummary({ summary }: { summary: SubmissionSummary }) {
  return (
    <article className="print-document rounded-lg border border-[#D94A16]/24 bg-white p-6 shadow-[0_24px_70px_rgba(45,32,8,.1)] sm:p-8">
      <header className="border-b border-[#D94A16]/22 pb-5">
        <p className="text-xs font-black uppercase tracking-[0.22em] text-[#D94A16]">{summary.businessName}</p>
        <h2 className="mt-2 font-display text-4xl font-bold text-[#050505]">{summary.formTitle}</h2>
        <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-2">
          <SummaryMeta label="Client name" value={summary.clientName} />
          <SummaryMeta label={summary.eventNameLabel || "Event name"} value={summary.eventName} />
          {summary.eventDate ? <SummaryMeta label={summary.eventDateLabel || "Event date"} value={summary.eventDate} /> : null}
          <SummaryMeta label="Date submitted" value={summary.submittedAt} />
        </dl>
      </header>

      <div className="mt-6 grid gap-6">
        {summary.sections.map((section) => (
          <section key={section.title} className="break-inside-avoid rounded-lg border border-[#D94A16]/18 bg-[#fffdf8] p-4">
            <h3 className="font-display text-2xl font-bold text-[#050505]">{section.title}</h3>
            <dl className="mt-4 grid gap-3">
              {section.answers.map((answer) => (
                <div key={`${section.title}-${answer.label}`} className="grid gap-1 border-t border-[#D94A16]/14 pt-3">
                  <dt className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{answer.label}</dt>
                  <dd className="whitespace-pre-wrap text-sm font-semibold leading-7 text-[#332a1f]">{answer.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </article>
  );
}

export function printSubmittedForm(summary: SubmissionSummary | null) {
  const previousTitle = document.title;
  if (summary) document.title = `${summary.formTitle} - ${summary.clientName}`;
  window.print();
  window.setTimeout(() => {
    document.title = previousTitle;
  }, 500);
}

export function RequiredMark() {
  return (
    <>
      <span className="font-black text-[#D94A16]" aria-hidden="true">
        *
      </span>
      <span className="sr-only"> required</span>
    </>
  );
}

function FieldLabel({ name, label, required }: { name: string; label: string; required?: boolean }) {
  return (
    <label htmlFor={name} className="text-sm font-black text-[#16120a]">
      {label} {required ? <RequiredMark /> : null}
    </label>
  );
}

function FieldError({ name, error }: { name: string; error?: string }) {
  return error ? (
    <p id={`${name}-error`} className="mt-2 text-sm font-bold text-red-700" role="alert">
      {error}
    </p>
  ) : null;
}

function SummaryMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[#D94A16]/18 bg-[#fff9ef] px-4 py-3">
      <dt className="text-xs font-black uppercase tracking-[0.14em] text-[#D94A16]">{label}</dt>
      <dd className="mt-1 font-bold leading-6 text-[#16120a]">{value}</dd>
    </div>
  );
}
